<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <NetworkProfileTable
        :create-enabled="true"
        :editable="true"
        :loading="isLoading"
        :removable="true"
        :rows="networkProfiles"
        :search-enabled="true"
        :search-query="searchQuery"
        :has-more-items="hasNextPage"
        @create="createModalVisible = true"
        @edit="editRow"
        @click-row="
          (networkProfile: NetworkProfile) => redirectToDetails(router, networkProfile.id, 'network-profiles.overview')
        "
        @remove="(networkProfile: NetworkProfile) => confirmRemove(networkProfile)"
        @search="(query: string) => search(query)"
        @load="!isFetching && fetchNextPage()"
      />

      <CreateNetworkProfileModal
        v-if="createModalVisible"
        :loading="isCreating"
        @create="(networkProfile: CreateNetworkProfile) => create(networkProfile)"
        @close="createModalVisible = false"
      />

      <UpdateNetworkProfileModal
        v-if="updateModalVisible && selectedRow"
        :network-profile="selectedRow"
        :loading="isUpdating"
        @update="(networkProfile: NetworkProfile) => update(networkProfile)"
        @close="updateModalVisible = false"
      />

      <InfuseConfirmModal
        v-if="removeModalVisible"
        :action-title="t('e_connector.networkProfiles.modal.remove.title')"
        verification-text="delete"
        @confirm="remove"
        @close="removeModalVisible = false"
      />
    </div>
  </InfusePageLayout>
</template>

<script setup lang="ts">
import { useNotify } from '@infuse/cpms/ui';
import { InfusePageLayout } from '@infuse/ui/layouts';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { replaceQueryParams } from '@infuse/ui/utils';
import { redirectToDetails } from '@infuse/e-connector/shared/utils';
import { NetworkProfile } from '@infuse/e-connector/network-profile/domain';
import {
  CreateNetworkProfileModal,
  NetworkProfileTable,
  UpdateNetworkProfileModal
} from '@infuse/e-connector/network-profile/ui';
import { useNetworkProfiles } from '@infuse/e-connector/network-profile/queries';
import {
  useCreateNetworkProfile,
  useDeleteNetworkProfile,
  useUpdateNetworkProfile
} from '@infuse/e-connector/network-profile/mutations';
import { CreateNetworkProfile } from '@infuse/api/e-connector/network-profile';
import { InfuseConfirmModal } from '@infuse/ui/components';

const { notifyFailure } = useNotify();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { networkProfiles, searchQuery, isLoading, hasNextPage, isFetching, fetchNextPage } = useNetworkProfiles();
const { mutateAsync: createNetworkProfile, isPending: isCreating } = useCreateNetworkProfile();
const { mutateAsync: updateNetworkProfile, isPending: isUpdating } = useUpdateNetworkProfile();
const { mutateAsync: deleteNetworkProfile } = useDeleteNetworkProfile();

const createModalVisible = ref<boolean>(false);
const updateModalVisible = ref<boolean>(false);
const removeModalVisible = ref<boolean>(false);
const selectedRow = ref<NetworkProfile>();

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

function search(search: string) {
  searchQuery.value = search;
  replaceQueryParams(router, 'network-profiles', { search: searchQuery.value });
}

async function create(networkProfile: CreateNetworkProfile) {
  await createNetworkProfile(networkProfile);
  createModalVisible.value = false;
}

async function update(networkProfile: NetworkProfile) {
  await updateNetworkProfile(networkProfile);
  updateModalVisible.value = false;
}

function editRow(row: NetworkProfile) {
  selectedRow.value = row;
  updateModalVisible.value = true;
}

function confirmRemove(row: NetworkProfile) {
  if (row.id === 1) {
    // prevent removal of default profile
    notifyFailure({
      title: t('e_connector.common.notify.error'),
      message: t('e_connector.networkProfiles.notify.remove.reason.defaultProfile')
    });
    return;
  }

  selectedRow.value = row;
  removeModalVisible.value = true;
}

async function remove() {
  if (!selectedRow.value) {
    return;
  }

  await deleteNetworkProfile(selectedRow.value.id);

  removeModalVisible.value = false;
  selectedRow.value = undefined;
}
</script>
